import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import FeatureGrid from '@src/components/DisposalPage/FeatureGrid'
import Highlight from '@src/components/DisposalPage/Highlight'
import Approach from '@src/components/DisposalPage/Approach'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import { withCompostingIcons } from '@src/constants/compostingIcons'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import austin from '../../json/commercial-composting-austin-data.json'

const austinServiceData = {
  name: 'Commercial Composting Services in Austin',
  description:
    "Commercial composting collection for Austin multifamily properties subject to the City of Austin's Universal Recycling Ordinance — one gallon of capacity per unit, weekly collection, compliant container placement, and documentation for the annual recycling plan.",
  serviceType: 'Commercial Composting',
  url: 'https://getdimension.com/commercial-composting/austin',
  areaServed: {
    '@type': 'City',
    name: 'Austin',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Austin',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
  },
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Multifamily Composting for URO Compliance' },
    { name: 'Outdoor Composting Bins' },
    { name: 'Valet Composting, Door to Door' },
    { name: 'Valet Composting Plus Trash Collection' },
  ],
}

const austinFAQData = {
  questions: austin.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function CommercialCompostingAustin() {
  return (
    <>
      <Head>
        <title>Commercial Composting Services in Austin, TX | Dimension</title>
        <meta
          name='description'
          content='Austin requires composting at multifamily properties with five or more units. Compliant bin capacity, weekly collection, placement, and plan documentation.'
        />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <link rel='canonical' href='https://getdimension.com/commercial-composting/austin' />
        <SchemaMarkup type='Organization' />
        <SchemaMarkup type='WebSite' />
        <SchemaMarkup type='Service' data={austinServiceData} />
        <SchemaMarkup type='FAQPage' data={austinFAQData} />
        <SchemaMarkup type='LocalBusiness' />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant='hazardous-waste'
          heroImage='compost'
          /* Heading already names Austin; appending a second city is nonsense. */
          disableCityLocalization
          heading={austin.pitch.heading}
          material={austin.pitch.material}
          pitchText={austin.pitch.pitchText}
          ctaLink={austin.pitch.ctaLink}
          ctaText={austin.pitch.ctaText}
          ctaHeading={austin.pitch.ctaHeading}
          ctaSubtext={austin.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <Highlight {...austin.uro} />
        <FeatureGrid {...austin.benefits} list={withCompostingIcons(austin.benefits.list)} />
        <SustainabilityDashboardPromo />
        <FeatureList {...austin.solutions} />
        <FeatureGrid {...austin.whatIs} list={withCompostingIcons(austin.whatIs.list)} />
        <Approach {...austin.approach} />
        <FAQ {...austin.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Commercial Composting',
              href: '/commercial-composting',
              description:
                'The nationwide composting service: packages, accepted materials, pricing model, and how a program is set up outside Austin.',
            },
            {
              label: 'Trash Valet',
              href: '/trash-valet',
              description:
                'Doorstep trash and recycling collection for multifamily properties — the amenity the door-to-door composting service runs alongside.',
            },
            {
              label: 'Residential Trash and Recycling',
              href: '/residential-trash-and-recycling',
              description:
                'Scheduled collection for apartments, HOAs, and rentals where city curbside service does not reach.',
            },
            {
              label: 'Recurring Waste Services',
              href: '/recurring-waste-services',
              description:
                'Set a cadence for every other stream the property produces and keep it on one schedule and one account.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default CommercialCompostingAustin
